import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { styled, type Theme } from '@mui/material';
import { type FC, type ReactElement, type ReactNode } from 'react';

const coastStepStates = ['default', 'notReached', 'pending', 'active', 'done', 'success', 'rejected'] as const;

type CoastStepState = (typeof coastStepStates)[number];
type CoastStepFill = 'filled' | 'outlined' | 'dashed';

type CoastStepIconProps = {
  state: CoastStepState;
  stepNumber?: number;
};

type StateConfig = {
  color: (theme: Theme) => string;
  fill: CoastStepFill;
  icon: ReactNode;
};

const stateConfig: Record<CoastStepState, StateConfig> = {
  default: { color: (theme) => theme.palette.text.disabled, fill: 'filled', icon: null },
  notReached: { color: (theme) => theme.palette.text.disabled, fill: 'outlined', icon: null },
  pending: { color: (theme) => theme.palette.text.disabled, fill: 'dashed', icon: null },
  active: { color: (theme) => theme.palette.primary.main, fill: 'outlined', icon: null },
  done: { color: (theme) => theme.palette.primary.main, fill: 'filled', icon: <CheckIcon fontSize="small" /> },
  success: { color: (theme) => theme.palette.success.main, fill: 'filled', icon: <CheckIcon fontSize="small" /> },
  rejected: { color: (theme) => theme.palette.error.main, fill: 'filled', icon: <CloseIcon fontSize="small" /> },
};

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'stepState',
})<{ stepState: CoastStepState }>(({ theme, stepState }) => {
  const { color, fill } = stateConfig[stepState];
  const resolved = color(theme);
  const isFilled = fill === 'filled';
  return {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderRadius: '50%',
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: isFilled ? resolved : 'transparent',
    borderWidth: 1,
    borderStyle: fill === 'dashed' ? 'dashed' : 'solid',
    borderColor: isFilled ? 'transparent' : resolved,
    color: isFilled ? theme.palette.common.white : resolved,
  };
});

const NumberText = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  lineHeight: 1,
}));

const CoastStepIcon: FC<CoastStepIconProps> = ({ state, stepNumber }): ReactElement => {
  const { icon } = stateConfig[state];

  return (
    <Root stepState={state} role="img" aria-label={`Step ${state}`} data-testid="coast-step-icon" data-state={state}>
      {icon ?? <NumberText>{stepNumber}</NumberText>}
    </Root>
  );
};

export { CoastStepIcon, coastStepStates, type CoastStepState, type CoastStepIconProps };
